import React, { useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FileText, Zap, MousePointer, CheckCircle, X } from 'lucide-react';
import { theme } from '../../styles/theme';
import { useWizardForm, WIZARD_STEPS } from '../../hooks/useWizardForm';
import { useAvailableFields } from '../../hooks/useAvailableFields';
import WizardStepper from './WizardStepper';
import StepBasic from './steps/StepBasic';
import StepTriggerData from './steps/StepTriggerData';
import StepTemplateVars from './steps/StepTemplateVars';
import StepButtons from './steps/StepButtons';
import StepSummary from './steps/StepSummary';

/**
 * normalizeCampaignToForm — converts a campaign object from the backend
 * into the wizard form state shape.
 */
function normalizeCampaignToForm(campaign) {
  if (!campaign) return null;

  // channels is an object like { whatsapp: { enabled, connectorId, templateId, variables } }
  // Extract the first active channel key plus its nested connectorId/templateId/variables
  let channel = '';
  let channelConnectorId = '';
  let channelTemplateId = '';
  let channelVariables = {};
  if (campaign.channels && typeof campaign.channels === 'object') {
    const keys = Object.keys(campaign.channels);
    const activeKey = keys.find((k) => campaign.channels[k]?.enabled) || keys[0] || '';
    if (activeKey) {
      channel = activeKey;
      const chData = campaign.channels[activeKey] || {};
      channelConnectorId = chData.connectorId || '';
      channelTemplateId = chData.templateId || '';
      channelVariables = chData.variables || {};
    }
  }

  // dataSource.params should be a JSON string for the form textarea
  const rawDataSource = campaign.dataSource || {};
  const dataSource = {
    connectorId: rawDataSource.connectorId || '',
    toolName: rawDataSource.toolName || '',
    contactField: rawDataSource.contactField || 'telefone',
    nameField: rawDataSource.nameField || 'nome',
    params: rawDataSource.params && typeof rawDataSource.params === 'object'
      ? JSON.stringify(rawDataSource.params, null, 2)
      : (rawDataSource.params || '{}'),
    filters: rawDataSource.filters || [],
  };

  return {
    name: campaign.name || '',
    instanceId: campaign.instanceId || '',
    channel,
    connectorId: channelConnectorId || campaign.connectorId || '',
    triggerConfig: campaign.triggerConfig || { type: 'manual' },
    payloadSchema: campaign.payloadSchema || null,
    resultPath: campaign.resultPath || '',
    dataSource,
    templateId: channelTemplateId || campaign.templateId || '',
    variables: channelVariables,
    variableMapping: campaign.variableMapping || {},
    buttonMappings: campaign.buttonMappings || {},
    agentFollowUpEnabled: campaign.agentFollowUpEnabled !== false,
    rateLimit: campaign.rateLimit || { batchSize: 30, delayMs: 0 },
    schedule: campaign.schedule || {
      enabled: campaign.triggerConfig?.type === 'scheduled' || campaign.scheduleStatus === 'scheduled',
      mode: campaign.cronExpression ? 'recurrent' : 'once',
      scheduledAt: campaign.scheduledAt || '',
      cronExpression: campaign.cronExpression || '',
    },
  };
}

/**
 * serializeFormToPayload — converts wizard form state to the backend API payload.
 */
function serializeFormToPayload(state) {
  // Parse params from JSON string to object
  let params = {};
  const rawParams = (state.dataSource.params || '{}').trim();
  if (rawParams && rawParams !== '{}') {
    try {
      params = JSON.parse(rawParams);
    } catch {
      params = {};
    }
  }

  const payload = {
    name: state.name,
    instanceId: state.instanceId,
    agentFollowUpEnabled: state.agentFollowUpEnabled,
    channels: {
      [state.channel]: {
        enabled: true,
        connectorId: state.connectorId,
        templateId: state.templateId,
        variables: state.variables,
      },
    },
    dataSource: {
      ...state.dataSource,
      params,
      filters: (state.dataSource.filters || []).filter(f => f.field),
    },
    rateLimit: state.rateLimit,
    triggerConfig: state.triggerConfig,
    schedule: state.schedule,
  };

  if (state.payloadSchema) {
    payload.payloadSchema = state.payloadSchema;
  }
  if (state.resultPath) {
    payload.resultPath = state.resultPath;
  }
  if (Object.keys(state.buttonMappings || {}).length > 0) {
    payload.buttonMappings = state.buttonMappings;
  }
  if (Object.keys(state.variableMapping || {}).length > 0) {
    payload.variableMapping = state.variableMapping;
  }

  return payload;
}

/**
 * CampaignWizard — top-level orchestrator component for the campaign wizard modal.
 *
 * Props:
 *   visible         - boolean controlling visibility
 *   onClose         - callback to close the modal
 *   onSave          - callback with serialized payload
 *   initialData     - campaign object for editing, or null for creating
 *   tenantId        - current tenant id
 *   agents          - array of agent instances
 *   connectors      - array of connector objects
 *   connectorTypes  - array of connector type definitions
 *   mktTemplates    - array of marketing templates
 */
export default function CampaignWizard({
  visible,
  onClose,
  onSave,
  initialData,
  tenantId,
  agents,
  connectors,
  connectorTypes,
  mktTemplates,
}) {
  const { t } = useTranslation('campaigns');
  const normalizedData = useMemo(
    () => normalizeCampaignToForm(initialData),
    [initialData]
  );

  const initialSnapshot = useRef(JSON.stringify(normalizedData));

  const {
    state,
    dispatch,
    currentStep,
    setStep,
    nextStep,
    prevStep,
    errors,
    validateStep,
  } = useWizardForm(normalizedData);

  // Derive the trigger type for available fields
  const triggerType = state.triggerConfig?.type || 'manual';

  // Lookup selected tool from connectors for useAvailableFields
  const selectedTool = useMemo(() => {
    if (!state.dataSource.connectorId || !state.dataSource.toolName) return null;
    const connector = (connectors || []).find(
      (c) => c.id === state.dataSource.connectorId || c.connectorId === state.dataSource.connectorId
    );
    if (!connector) return null;
    // Enrich with tools from connector type
    const ct = (connectorTypes || []).find((t) => t.type === connector.type);
    const tools = ct?.capabilities?.tools || connector.tools || [];
    return tools.find((t) => t.name === state.dataSource.toolName) || null;
  }, [connectors, connectorTypes, state.dataSource.connectorId, state.dataSource.toolName]);

  const availableFields = useAvailableFields(
    triggerType,
    state.payloadSchema,
    selectedTool,
    state.resultPath || ''
  );

  // Compute completed and error steps for the stepper
  const completedSteps = useMemo(() => {
    const completed = new Set();
    for (let i = 0; i < WIZARD_STEPS.length; i++) {
      const stepErrors = validateStep(i);
      if (Object.keys(stepErrors).length === 0) {
        // Only mark as completed if all previous steps are also completed
        // This prevents later steps (with no required fields) from always being green
        const allPreviousCompleted = i === 0 || completed.has(i - 1);
        if (allPreviousCompleted) {
          completed.add(i);
        }
      }
    }
    return completed;
  }, [validateStep]);

  const errorSteps = useMemo(() => {
    const withErrors = new Set();
    for (let i = 0; i < WIZARD_STEPS.length; i++) {
      if (i === currentStep) continue;
      // Only show error state for steps the user has already passed (before current step)
      if (i > currentStep) continue;
      const stepErrors = validateStep(i);
      if (Object.keys(stepErrors).length > 0) {
        withErrors.add(i);
      }
    }
    return withErrors;
  }, [validateStep, currentStep]);

  // Steps config with icons for the stepper
  const stepsConfig = useMemo(() => [
    { key: 'basic', label: t('wizard.stepBasic'), icon: <FileText size={18} /> },
    { key: 'trigger', label: t('wizard.stepTrigger'), icon: <Zap size={18} /> },
    { key: 'template', label: t('wizard.stepTemplate'), icon: <FileText size={18} /> },
    { key: 'buttons', label: t('wizard.stepButtons'), icon: <MousePointer size={18} /> },
    { key: 'summary', label: t('wizard.stepSummary'), icon: <CheckCircle size={18} /> },
  ], [t]);

  // Unsaved changes check
  const hasUnsavedChanges = useCallback(() => {
    const currentSnapshot = JSON.stringify(state);
    const initial = initialSnapshot.current;
    // Compare against initial normalized data or empty form state
    if (!initial || initial === 'null') {
      // Creating new — check if any meaningful data was entered
      return state.name !== '' || state.instanceId !== '' || state.channel !== '';
    }
    return currentSnapshot !== initial;
  }, [state]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      // eslint-disable-next-line no-alert
      const discard = window.confirm(t('wizard.discardConfirm'));
      if (!discard) return;
    }
    onClose();
  }, [hasUnsavedChanges, onClose, t]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  const handleSave = useCallback(() => {
    const payload = serializeFormToPayload(state);
    onSave(payload);
  }, [state, onSave]);

  const handleStepClick = useCallback((stepIndex) => {
    setStep(stepIndex);
  }, [setStep]);

  // Don't render if not visible
  if (!visible) return null;

  const isLastStep = currentStep === WIZARD_STEPS.length - 1;
  const isFirstStep = currentStep === 0;
  const title = initialData ? t('wizard.editTitle') : t('wizard.newTitle');

  return (
    <Backdrop onClick={handleBackdropClick} aria-modal="true" role="dialog" aria-label={title}>
      <ModalContent>
        <Header>
          <Title>{title}</Title>
          <CloseButton
            type="button"
            onClick={handleClose}
            aria-label={t('wizard.close')}
          >
            <X size={20} />
          </CloseButton>
        </Header>

        <WizardStepper
          steps={stepsConfig}
          currentStep={currentStep}
          completedSteps={completedSteps}
          errorSteps={errorSteps}
          onStepClick={handleStepClick}
        />

        <StepContent>
          {currentStep === 0 && (
            <StepBasic
              state={state}
              dispatch={dispatch}
              errors={errors}
              agents={agents || []}
              connectors={connectors || []}
            />
          )}
          {currentStep === 1 && (
            <StepTriggerData
              state={state}
              dispatch={dispatch}
              errors={errors}
              connectors={connectors || []}
              connectorTypes={connectorTypes || []}
              availableFields={availableFields}
              agents={agents || []}
            />
          )}
          {currentStep === 2 && (
            <StepTemplateVars
              state={state}
              dispatch={dispatch}
              errors={errors}
              mktTemplates={mktTemplates || []}
              connectors={connectors || []}
              availableFields={availableFields}
            />
          )}
          {currentStep === 3 && (
            <StepButtons
              state={state}
              dispatch={dispatch}
              errors={errors}
              availableFields={availableFields}
              mktTemplates={mktTemplates || []}
              connectors={connectors || []}
              connectorTypes={connectorTypes || []}
              agents={agents || []}
            />
          )}
          {currentStep === 4 && (
            <StepSummary
              state={state}
              errors={errors}
              agents={agents || []}
              connectors={connectors || []}
              mktTemplates={mktTemplates || []}
              onNavigateToStep={handleStepClick}
            />
          )}
        </StepContent>

        <Footer>
          <FooterLeft>
            {!isFirstStep && (
              <BackButton type="button" onClick={prevStep}>
                {t('wizard.back')}
              </BackButton>
            )}
          </FooterLeft>
          <FooterRight>
            {!isLastStep && (
              <NextButton type="button" onClick={nextStep}>
                {t('wizard.next')}
              </NextButton>
            )}
            {isLastStep && (
              <SaveButton type="button" onClick={handleSave}>
                {t('wizard.save')}
              </SaveButton>
            )}
          </FooterRight>
        </Footer>
      </ModalContent>
    </Backdrop>
  );
}

// ─── Styled Components ───────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${theme.zIndex.modal};
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 0;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  background: ${theme.colors.white};
  border-radius: ${theme.radii.lg};
  box-shadow: ${theme.shadows.xl};
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 100%;
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[5]} ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-size: ${theme.typography.xl};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: ${theme.radii.md};
  background: transparent;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  transition: ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.bgSecondary};
    color: ${theme.colors.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const StepContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[4]};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
  }
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const buttonBase = `
  padding: ${theme.spacing[2]} ${theme.spacing[5]};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: ${theme.transitions.fast};

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const BackButton = styled.button`
  ${buttonBase}
  background: transparent;
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textSecondary};

  &:hover {
    background: ${theme.colors.bgSecondary};
    border-color: ${theme.colors.borderHover};
    color: ${theme.colors.textPrimary};
  }
`;

const NextButton = styled.button`
  ${buttonBase}
  background: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary};
  color: ${theme.colors.textInverse};

  &:hover {
    background: ${theme.colors.primaryHover};
    border-color: ${theme.colors.primaryHover};
  }
`;

const SaveButton = styled.button`
  ${buttonBase}
  background: ${theme.colors.success};
  border: 1px solid ${theme.colors.success};
  color: ${theme.colors.textInverse};

  &:hover {
    background: #0d9668;
    border-color: #0d9668;
  }
`;
